import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {
  ProcessFlowchartEdgeType,
  TransitionSource,
  TaskSchemaNodeSection,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  getDefaultTransitionSource,
  getNodeSectionTypeFromDataFlowEdge,
  getTransitionSourceByNodeIdPairKey,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {ORANGE_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {findAllUpstreamMappableNodeIds} from '../TraverseFlowchart';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';

function getOptionTextForTransitionSource(transitionSource: TransitionSource): string {
  switch (transitionSource) {
    case TransitionSource.OUTPUTS:
      return 'One task per output in previous step';
    case TransitionSource.INPUTS:
      return 'One task per task in previous step';
    default:
      throw new Error(`Unexpected transition source: ${transitionSource}`);
  }
}

export interface FlowchartSettingsTabContentProps {
  locked?: boolean;
}

interface SettingsContentProps {
  selectedNodeName: string;
  connectedNodes: Array<TaskSchemaNode>;
  transitionSources: Record<string, TransitionSource>;
  handleTransitionSourceSelect: (upstreamMappableNodeIdToUpdate: string, value: TransitionSource) => void;
  locked?: boolean;
}

function EmptySettingsContent(props: {selectedNodeName: string}): JSX.Element {
  const {selectedNodeName} = props;
  return (
    <div className={css(styles.emptyStateContainer)}>
      <div className={css(styles.flowchartSettingDescription)}>
        There are no mappings to configure for{' '}
        <span className={css(styles.flowchartSettingDescriptionBold)}>{selectedNodeName}</span>.
      </div>
    </div>
  );
}

function SettingsContent(props: SettingsContentProps): JSX.Element {
  const {selectedNodeName, connectedNodes, transitionSources, handleTransitionSourceSelect, locked} = props;

  return (
    <>
      <div className={css(styles.flowchartSettingDescription)}>
        <span>Create tasks in </span>
        <Chip text={selectedNodeName} size={ChipSize.MINI} />
        <span>based on the number of</span>
        <span className={css(styles.flowchartSettingDescriptionBold)}>outputs</span>
        <span> or</span>
        <span className={css(styles.flowchartSettingDescriptionBold)}>tasks</span>
        <span> created in the previous step(s).</span>
      </div>
      <Table
        numRows={connectedNodes.length}
        calculateInView={false}
        fitContent={false}
        frozenHeader={false}
        getRowKey={(rowIndex) => connectedNodes[rowIndex].id}
        includeNegativeMargins={false}
        isCompact={false}
        overflowVisible={false}
      >
        <Column
          header="Previous step"
          renderCell={(rowIdx: number) => {
            const node = connectedNodes[rowIdx];
            return <Cell>{node.data.nodeName}</Cell>;
          }}
        />
        <Column
          header="Task creation behavior"
          renderCell={(rowIdx: number) => {
            const node = connectedNodes[rowIdx];
            const hasOutputSection = node.data.sections.some(
              (section) => section.sectionName === TaskSchemaNodeSection.OUTPUTS
            );
            const availableTransitionSources = hasOutputSection
              ? Object.values(TransitionSource)
              : [TransitionSource.INPUTS];

            return (
              <Cell className={css(styles.tableCell)}>
                <SelectDropdown
                  menu={
                    <Menu
                      height={Menu.HEIGHTS.MEDIUM}
                      width={Menu.WIDTHS.LARGE}
                      value={transitionSources[node.id]}
                    >
                      {availableTransitionSources.map((transitionSource) => (
                        <Option
                          key={transitionSource}
                          value={transitionSource}
                          onSelect={(value: TransitionSource) => {
                            handleTransitionSourceSelect(node.id, value);
                          }}
                          isDisabled={locked}
                        >
                          {getOptionTextForTransitionSource(transitionSource)}
                        </Option>
                      ))}
                    </Menu>
                  }
                  displayName={getOptionTextForTransitionSource(transitionSources[node.id])}
                />
                {transitionSources[node.id] === TransitionSource.INPUTS && (
                  <div>
                    <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
                    <span className={css(styles.secondaryText)}>
                      Limits field mappings to input fields from the previous step
                    </span>
                  </div>
                )}
              </Cell>
            );
          }}
        />
      </Table>
    </>
  );
}

export default function FlowchartSettingsPane(props: FlowchartSettingsTabContentProps): JSX.Element {
  const {
    selectedNode,
    interactiveFlowchart,
    transitionSourceByNodeIdPair,
    onChangeTransitionSource,
    onChangeFlowchart,
    onChangeCustomMappingData,
  } = NodeDetailsSidepanelContext.useContext();

  const {upstreamMappableNodeIds, transitionSources} = useMemo(() => {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
    const transitionSources: Record<string, TransitionSource> = {};
    for (const sourceNodeId of upstreamMappableNodeIds) {
      const nodeIdPair = getTransitionSourceByNodeIdPairKey(sourceNodeId, selectedNode.id);
      transitionSources[sourceNodeId] =
        transitionSourceByNodeIdPair[nodeIdPair] ??
        getDefaultTransitionSource(
          sourceNodeId,
          interactiveFlowchart.nodes.map((node) => node.data)
        );
    }
    return {upstreamMappableNodeIds, transitionSources};
  }, [interactiveFlowchart, selectedNode.id, transitionSourceByNodeIdPair]);

  const {nodes, edges} = interactiveFlowchart;
  const connectedNodes = Object.values(nodes).filter((node) => upstreamMappableNodeIds.includes(node.id));

  const handleTransitionSourceSelect = (
    upstreamMappableNodeIdToUpdate: string,
    value: TransitionSource
  ): void => {
    const currentTransitionSource = transitionSources[upstreamMappableNodeIdToUpdate];
    const nodeIdPair = getTransitionSourceByNodeIdPairKey(upstreamMappableNodeIdToUpdate, selectedNode.id);
    if (currentTransitionSource === TransitionSource.OUTPUTS && value === TransitionSource.INPUTS) {
      // If we are changing the transition source from outputs to inputs, we need to clear all mappings from
      // the output fields of the source node.
      const dataFlowEdgesToClear = edges.filter((edge) => {
        if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
          return false;
        }
        if (edge.target !== selectedNode.id || edge.source !== upstreamMappableNodeIdToUpdate) {
          return false;
        }
        const sourceFieldSectionType = getNodeSectionTypeFromDataFlowEdge(
          edge,
          nodes.map((node) => node.data)
        );
        return sourceFieldSectionType === TaskSchemaNodeSectionType.OUTPUTS;
      });

      for (const edge of dataFlowEdgesToClear) {
        onChangeCustomMappingData(edge.id, {});
      }

      onChangeFlowchart({
        nodeChanges: [],
        edgeChanges: dataFlowEdgesToClear.map((edge) => ({id: edge.id, type: 'remove'})),
      });
    }
    onChangeTransitionSource(nodeIdPair, value);
  };

  return (
    <div className={css(styles.tabContentContainer)} key={selectedNode.id}>
      <div className={css(styles.settingsGroup)}>
        <div className={css(styles.flowchartSettingTitle)}>Task creation behavior</div>
        {connectedNodes.length === 0 ? (
          <EmptySettingsContent selectedNodeName={selectedNode.data.nodeName} />
        ) : (
          <SettingsContent
            selectedNodeName={selectedNode.data.nodeName}
            connectedNodes={connectedNodes}
            transitionSources={transitionSources}
            handleTransitionSourceSelect={handleTransitionSourceSelect}
            locked={props.locked}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  tabContentContainer: {
    padding: '16px',
    marginTop: '-23px',
  },
  settingsGroup: {},
  flowchartSettingTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: Color.GRAY_900,
    marginBottom: '4px',
  },
  flowchartSettingDescription: {
    fontSize: '14px',
    fontWeight: 400,
    color: Color.GRAY_900,
    flexWrap: 'nowrap',
    marginBottom: '10px',
  },
  flowchartSettingDescriptionBold: {
    fontWeight: 600,
    paddingLeft: '4px',
  },
  tableCell: {
    lineHeight: '32px',
  },
  primaryText: {
    color: Color.GRAY_900,
  },
  secondaryText: {
    color: Color.GRAY_600,
  },
  warningIcon: {
    paddingRight: 4,
    color: ORANGE_DARK,
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
});
